import { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { supabase } from './supabase';
import appConfig from './appConfig';

export default function GoogleSync({ onClose, onSyncComplete }) {
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [done, setDone] = useState(false);
  const [stats, setStats] = useState({ added: 0, skipped: 0 });

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setSyncing(true);
      setStatus('Connected to Google! Fetching contacts...');
      try {
        await syncContacts(tokenResponse.access_token);
      } catch (err) {
        setStatus('Error: ' + err.message);
        setSyncing(false);
      }
    },
    onError: () => {
      setStatus('Google login failed. Please try again.');
      setSyncing(false);
    },
    scope: 'https://www.googleapis.com/auth/contacts.readonly',
  });

  async function syncContacts(accessToken) {
    var allContacts = [];
    var nextPageToken = null;

    do {
      var url = 'https://people.googleapis.com/v1/people/me/connections' +
        '?personFields=names,phoneNumbers,emailAddresses,addresses' +
        '&pageSize=1000' +
        (nextPageToken ? '&pageToken=' + nextPageToken : '');

      var response = await fetch(url, {
        headers: { Authorization: 'Bearer ' + accessToken }
      });
      var data = await response.json();
      if (data.connections) {
        allContacts = allContacts.concat(data.connections);
      }
      nextPageToken = data.nextPageToken;
    } while (nextPageToken);

    setStatus('Found ' + allContacts.length + ' contacts. Syncing to CRM...');

    var toInsert = [];
    var skipped = 0;

    for (var i = 0; i < allContacts.length; i++) {
      var contact = allContacts[i];
      var names = contact.names || [];
      var phones = contact.phoneNumbers || [];
      var emails = contact.emailAddresses || [];
      var addrs = contact.addresses || [];

      if (phones.length === 0) { skipped++; continue; }

      var fullName = names.length > 0 ? names[0].displayName : 'Unknown';
      var phone = phones[0].value.replace(/\D/g, '');
      var email = emails.length > 0 ? emails[0].value : '';
      var address = addrs.length > 0 ? (addrs[0].streetAddress || '') : '';
      var city = addrs.length > 0 ? (addrs[0].city || '') : '';

      toInsert.push({
        full_name: fullName,
        phone: phone,
        email: email,
        address: address ? address + (city ? ', ' + city : '') : '',
        area: city,
        customer_type: 'lead',
        company_id: appConfig.companyId,
        bulk_imported: true
      });

      setProgress(Math.round((i / allContacts.length) * 50));
    }

    var batchSize = 50;
    var inserted = 0;
    for (var j = 0; j < toInsert.length; j += batchSize) {
      var batch = toInsert.slice(j, j + batchSize);
      await supabase.from('customers').upsert(batch, { onConflict: 'company_id,phone', ignoreDuplicates: true }); // lint-ignore-company-id — batch rows already include company_id, built above
      inserted += batch.length;
      setProgress(50 + Math.round((inserted / toInsert.length) * 50));
    }

    setStats({ added: inserted, skipped });
    setStatus('Sync complete!');
    setSyncing(false);
    setDone(true);
    if (onSyncComplete) onSyncComplete();
  }

  return (
    <div style={{ fontFamily: 'Arial', maxWidth: 600, margin: '0 auto', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onClose}
          style={{ padding: '10px 20px', background: '#f0f0f0', color: '#333',
            border: '1.5px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 'bold' }}>
          ← Back
        </button>
        <h2 style={{ margin: 0, fontSize: 18 }}>Sync Google Contacts</h2>
      </div>

      <div style={{ background: '#E8F0FD', border: '1px solid #1A73C8', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <p style={{ margin: '0 0 8px', fontWeight: 'bold', color: '#1A73C8', fontSize: 14 }}>
          📱 How this works:
        </p>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#333' }}>1. Click the button below to connect your Google account</p>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#333' }}>2. Google will ask permission to read your contacts</p>
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#333' }}>3. All contacts with phone numbers will sync to CRM</p>
        <p style={{ margin: 0, fontSize: 13, color: '#333' }}>4. New contacts added to your phone appear here automatically</p>
      </div>

      {!done && !syncing && (
        <button onClick={function() { login(); }}
          style={{ width: '100%', padding: 16, background: '#1A73C8', color: 'white',
            border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer', fontWeight: 'bold',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          🔗 Connect Google Contacts
        </button>
      )}

      {syncing && (
        <div>
          <p style={{ color: '#1A73C8', fontWeight: 'bold', fontSize: 14 }}>{status}</p>
          <div style={{ background: '#f0f0f0', borderRadius: 8, height: 12, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ background: '#1A73C8', height: '100%', width: progress + '%', transition: 'width 0.3s' }}/>
          </div>
          <p style={{ textAlign: 'center', fontSize: 13, color: '#666' }}>{progress}%</p>
        </div>
      )}

      {done && (
        <div>
          <div style={{ background: '#E6F4EF', border: '2px solid #1D9E75', borderRadius: 12,
            padding: 20, textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <h3 style={{ margin: '0 0 8px', color: '#1D9E75' }}>Sync Complete!</h3>
            <p style={{ margin: '0 0 4px', fontSize: 14, color: '#333' }}>
              <strong>{stats.added}</strong> contacts added to CRM
            </p>
            <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
              {stats.skipped} contacts skipped (no phone number)
            </p>
          </div>
          <button onClick={onClose}
            style={{ width: '100%', padding: 14, background: '#1D9E75', color: 'white',
              border: 'none', borderRadius: 8, fontSize: 15, cursor: 'pointer', fontWeight: 'bold' }}>
            Go to Contacts
          </button>
        </div>
      )}
    </div>
  );
}